import React, {
    type Dispatch,
    type SetStateAction, useCallback,
} from 'react';


import {DateRangeInput, FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {useFormatInputValue} from 'src/hooks/useFormatInputValue';

import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {
    CONFERENCE_NAME_CONTROL,
    END_DATE_CONTROL,
    START_DATE_CONTROL,
} from '../constants';

import type {Error} from 'src/openapi/admin/models/Error';
import type {UpdateConferenceRequest} from 'src/openapi/admin/models/UpdateConferenceRequest';

const SECTION_NAME = 'Conference Information';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    data: UpdateConferenceRequest,
    setData: Dispatch<SetStateAction<UpdateConferenceRequest>>,
    trackParameterChange: (parameter: string) => void,
    readonly?: boolean,
};

export const DetailsView = (props: Props) => {
    const {isLoading, loadingError, data, setData, trackParameterChange, readonly} = props;

    useFormatInputValue({
        value: data.name,
        setValue: conferenceName => setData(prev => ({...prev, name: conferenceName})),
        regex: /[^0-9 A-Za-z$&+,:{};_=?@#|'\]<>.^*()%!-\\/]/g,
    });

    const changeStartDate = useCallback((date: string) => {
        setData(prev => ({...prev, startDate: date}));
        trackParameterChange('Start Date');
    }, [setData, trackParameterChange]);

    const changeEndDate = useCallback((date: string) => {
        setData(prev => ({...prev, endDate: date}));
        trackParameterChange('End Date');
    }, [setData, trackParameterChange]);

    const changeConferenceName = useCallback((name: string) => {
        setData(prev => ({...prev, name}));
        trackParameterChange('Conference Name');
    }, [setData, trackParameterChange]);

    return (
        <Skeleton shown={isLoading} view="">
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}>
                <FormSection headline={SECTION_NAME}>
                    <Group direction="v">
                        <CustomValidatedControl name={CONFERENCE_NAME_CONTROL}>
                            {error => (
                                <WidthItem width={300}>
                                    <Label htmlFor="conference-name-input">
                                        <RequiredMark label="Conference name"/>
                                    </Label>
                                    <FormInput
                                        id="conference-name-input"
                                        maxLength={255}
                                        readOnly={readonly}
                                        placeholder="Conference name"
                                        value={data.name}
                                        valid={error ? false : undefined}
                                        errorDescription={error}
                                        onBlur={changeConferenceName}
                                    />
                                </WidthItem>
                            )}
                        </CustomValidatedControl>
                        <Group direction="v" gap="s">
                            <Label><RequiredMark label="Сonference Period"/></Label>
                            <ValidatedControl name={START_DATE_CONTROL}>
                                {(invalid: boolean) => (
                                    <WidthItem width={300}>
                                        <DateRangeInput
                                            id="conference-start-date-picker"
                                            readonly={readonly}
                                            width={300}
                                            label="Starts"
                                            invalid={invalid}
                                            clearable={true}
                                            startDate={data.startDate}
                                            endDate={data.endDate}
                                            selectRangeType="start"
                                            onChange={changeStartDate}
                                        />
                                    </WidthItem>
                                )}
                            </ValidatedControl>
                            <ValidatedControl name={END_DATE_CONTROL}>
                                {(invalid: boolean) => (
                                    <WidthItem width={300}>
                                        <DateRangeInput
                                            id="conference-end-date-picker"
                                            readonly={readonly}
                                            width={300}
                                            label={<>Ends&nbsp;&nbsp;</>}
                                            invalid={invalid}
                                            clearable={true}
                                            preventPastDate={true}
                                            startDate={data.startDate}
                                            endDate={data.endDate}
                                            selectRangeType="end"
                                            onChange={changeEndDate}
                                        />
                                    </WidthItem>
                                )}
                            </ValidatedControl>
                        </Group>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
